import { Gavel } from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  ListItemButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Search as SearchBase,
  useDebounce,
  useLocalStorage,
  useSearch,
} from "@ssf/components";
import { searchClubs, searchMembers } from "@ssf/memdb-api";
import { useQuery } from "@tanstack/react-query";
import Fuse from "fuse.js";
import { Account, Draw, Magnify, Plus } from "mdi-material-ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { generatePath } from "../lib/routes.js";
import {
  clubSearchHit,
  combinedSearchHits,
  memberSearchHits,
  rememberSearch,
  type SearchHit,
  type SearchMode,
  searchHitHref,
} from "../lib/searchOptions.js";

const modes = [
  ["all", Magnify],
  ["club", Gavel],
  ["member", Account],
] as const;

type Action = {
  type: "action";
  id: "clubs" | "signatures" | "createClub";
  name: string;
  href: string;
};
type Option = SearchHit | Action;

function hrefFor(option: Option) {
  return option.type === "action" ? option.href : searchHitHref(option);
}

const SearchItem = ({
  option,
  onSelect,
}: {
  option: Option;
  onSelect(props: { option: Option }): void;
}) => {
  const href = hrefFor(option);
  const Icon =
    option.type === "member"
      ? Account
      : option.type === "action" && option.id === "createClub"
        ? Plus
        : option.type === "action" && option.id === "signatures"
          ? Draw
          : Gavel;
  const secondary = option.type === "action" ? undefined : option.secondary;

  return (
    <ListItemButton
      disabled={!href}
      {...(href && { component: Link, to: href })}
      onClick={(event) => {
        if (
          !event.metaKey &&
          !event.ctrlKey &&
          !event.shiftKey &&
          !event.altKey
        ) {
          event.preventDefault();
          onSelect({ option });
        }
      }}
      sx={{ ":hover": { backgroundColor: "transparent" } }}
    >
      <Icon
        fontSize="small"
        sx={{ mr: 1.5, color: ({ palette }) => palette.text.secondary }}
      />
      <Box
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography>{option.name}</Typography>
        {secondary && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            {secondary}
          </Typography>
        )}
      </Box>
    </ListItemButton>
  );
};

export const Search = () => {
  const { t } = useTranslation("Search");
  const navigate = useNavigate();
  const [mode, setMode] = useState<SearchMode | "all">("all");
  const searchProps = useSearch();
  const searchTerm = searchProps.value.trim();
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const canSearch =
    searchTerm.length >= 2 && searchTerm === debouncedSearchTerm;

  const [recentSearches, setRecentSearches] = useLocalStorage<SearchHit[]>(
    "recentSearchesCombined",
    [],
  );
  const actions = useMemo<Action[]>(
    () => [
      {
        type: "action",
        id: "clubs",
        name: t("actions.clubs"),
        href: generatePath("clubs", {}),
      },
      {
        type: "action",
        id: "signatures",
        name: t("actions.signatures"),
        href: generatePath("signatures", {}),
      },
      {
        type: "action",
        id: "createClub",
        name: t("actions.createClub"),
        href: generatePath("createClub", {}),
      },
    ],
    [t],
  );
  const actionSearch = useMemo(
    () => new Fuse(actions, { keys: ["name"] }),
    [actions],
  );

  const clubs = useQuery({
    queryKey: ["memdbSearchV3", "club", debouncedSearchTerm, 10],
    queryFn: async () =>
      (await searchClubs({ q: debouncedSearchTerm, limit: 10 })).map(
        clubSearchHit,
      ),
    enabled: canSearch && mode !== "member",
  });
  const members = useQuery({
    queryKey: ["memdbSearchV3", "member", debouncedSearchTerm, 50, 10],
    queryFn: async () =>
      memberSearchHits(
        await searchMembers({ q: debouncedSearchTerm, limit: 50 }),
      ),
    enabled: canSearch && mode !== "club",
  });

  const activeQueries = [
    ...(mode !== "member" ? [{ type: "club" as const, query: clubs }] : []),
    ...(mode !== "club" ? [{ type: "member" as const, query: members }] : []),
  ];
  const isFetching = activeQueries.some(({ query }) => query.isFetching);
  const isError = activeQueries.some(({ query }) => query.isError);
  function hitsFor(type: SearchMode, data: SearchHit[] | undefined) {
    if (mode !== "all" && mode !== type) return [];
    if (!searchTerm) return recentSearches.filter((hit) => hit.type === type);
    return canSearch ? (data ?? []) : [];
  }
  const hits = combinedSearchHits(
    hitsFor("club", clubs.data),
    hitsFor("member", members.data),
    searchTerm,
  );

  const options: Option[] = [
    ...hits,
    ...(searchTerm
      ? actionSearch.search(searchTerm).map(({ item }) => item)
      : actions),
  ];

  function handleSelect({ option }: { option: Option }) {
    const href = hrefFor(option);
    if (!href) return;
    if (option.type !== "action") {
      setRecentSearches((previous) => rememberSearch(option, previous));
    }
    searchProps.setOpen(false);
    navigate(href);
  }

  return (
    <Box sx={{ display: "flex", width: "100%", justifyContent: "center" }}>
      {!searchProps.open && (
        <IconButton
          component="label"
          htmlFor="search-box"
          sx={({ breakpoints }) => ({
            ml: "auto",
            [breakpoints.up("md")]: {
              display: "none",
            },
          })}
        >
          <Magnify />
        </IconButton>
      )}
      <Box
        sx={({ breakpoints }) => ({
          position: "absolute",
          zIndex: 10,
          mx: 2,
          height: 40,
          opacity: searchProps.open ? 1 : 0,
          pointerEvents: searchProps.open ? "auto" : "none",
          top: 8,
          left: 0,
          right: 0,
          [breakpoints.up("md")]: {
            opacity: 1,
            pointerEvents: "auto",
            position: "relative",
            maxWidth: 600,
            width: "100%",
            top: 0,
          },
        })}
      >
        <SearchBase
          key={mode}
          options={options}
          renderItem={(props) => (
            <Box sx={{ width: "100%", minWidth: 0 }}>
              {options[props.index - 1]?.type !== props.option.type && (
                <Typography
                  component="div"
                  variant="overline"
                  color="text.secondary"
                  sx={{ px: 2, pt: 1, bgcolor: "background.paper" }}
                >
                  {t(`sections.${props.option.type}`)}
                </Typography>
              )}
              <SearchItem {...props} onSelect={handleSelect} />
            </Box>
          )}
          getKey={({ option }) => `${option.type}-${option.id}`}
          placeholder={t(`placeholders.${mode}`)}
          onSelect={handleSelect}
          noOptions={
            searchProps.open &&
            searchTerm && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {searchTerm.length < 2
                  ? t("minCharacters")
                  : !canSearch || isFetching
                    ? t("loading")
                    : isError
                      ? t("error")
                      : t("noResults")}
              </Typography>
            )
          }
          {...searchProps}
        >
          <Divider orientation="vertical" sx={{ height: 24, mx: 1 }} />
          {modes.map(([value, Icon]) => (
            <Tooltip key={value} title={t(`tooltips.${value}`)}>
              <IconButton
                aria-label={t(`tooltips.${value}`)}
                aria-pressed={mode === value}
                color={mode === value ? "primary" : "default"}
                onClick={(event) => {
                  event.preventDefault();
                  setMode(value);
                  searchProps.setOpen(true);
                }}
              >
                <Icon fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
        </SearchBase>
        {searchProps.open &&
          canSearch &&
          options.length > 0 &&
          activeQueries.map(
            ({ type, query }) =>
              (query.isFetching || query.isError) && (
                <Typography
                  key={type}
                  component="output"
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    px: 2,
                    py: 1,
                    bgcolor: "background.paper",
                  }}
                >
                  {t(`sections.${type}`)}:{" "}
                  {t(query.isFetching ? "loading" : "error")}
                </Typography>
              ),
          )}
      </Box>
    </Box>
  );
};
